import BoltIcon from '@mui/icons-material/Bolt';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import SavingsIcon from '@mui/icons-material/Savings';
import { Grid } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
// eslint-disable-next-line no-restricted-imports -- pre-existing direct DataGridPro use; migrate to ServerDataGrid when touched
import { DataGridPro } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useScope, useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import CustomerTitleItems from '@sophocles/components/App/CustomerTitleItems';
import TitleBar from '@sophocles/components/App/TitleBar';
import StatCard, { StatCardGroup } from '@sophocles/components/StatCard';
import {
  fetchManagementDashboard,
  selectManagementData,
  selectManagementLoading,
  selectManagementError,
} from '@sophocles/features/dashboards/slice';
import { getCustomerTerminologies } from '@sophocles/utils/customerUtils';

const PERIOD_OPTIONS = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '1y', label: '1y' },
];

function ManagementDashboardPage(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const customerId = params.cust_id;
  const { customer } = useScope();
  const { user, isStaff } = useUser();
  const data = useSelector(selectManagementData);
  const loading = useSelector(selectManagementLoading);
  const error = useSelector(selectManagementError);

  const [period, setPeriod] = useState('30d');

  const fetchData = useCallback(() => {
    if (customerId) {
      dispatch(fetchManagementDashboard({ customerId, period }));
    }
  }, [dispatch, customerId, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePeriodChange = (_, newPeriod) => {
    if (newPeriod !== null) setPeriod(newPeriod);
  };

  const energyChartData = useMemo(() => {
    if (!data?.energy_consumption?.by_site_group) return [];
    return data.energy_consumption.by_site_group.map(sg => ({
      name: sg.name,
      kwh: sg.kwh,
    }));
  }, [data]);

  const ledVsHalogenData = useMemo(() => {
    if (!data?.led_vs_halogen) return [];
    const d = data.led_vs_halogen;
    return [
      { name: t('managementDashboard.led', { defaultValue: 'LED' }), count: d.led_count, avgWatts: d.led_avg_watts },
      {
        name: t('managementDashboard.halogenHps', { defaultValue: 'Halogen/HPS' }),
        count: d.halogen_count,
        avgWatts: d.halogen_avg_watts,
      },
    ];
  }, [data, t]);

  const terms = useMemo(() => getCustomerTerminologies(customer, t), [customer, t]);

  const noisyColumns = useMemo(
    () => [
      { field: 'site_name', headerName: terms.site, flex: 1, minWidth: 150 },
      {
        field: 'alarm_count',
        headerName: t('managementDashboard.columnAlarms', { defaultValue: 'Alarms' }),
        width: 100,
        type: 'number',
      },
      {
        field: 'top_alarms',
        headerName: t('managementDashboard.columnTopAlarms', { defaultValue: 'Top Alarms' }),
        flex: 1,
        minWidth: 200,
        valueFormatter: value => (value || []).join(', ').replace(/_/g, ' '),
      },
    ],
    [terms, t],
  );

  const outlierColumns = useMemo(
    () => [
      { field: 'site_name', headerName: terms.site, flex: 1, minWidth: 150 },
      {
        field: 'expected_watts',
        headerName: t('managementDashboard.columnExpectedWatts', { defaultValue: 'Expected (W)' }),
        width: 130,
        type: 'number',
      },
      {
        field: 'actual_watts',
        headerName: t('managementDashboard.columnActualWatts', { defaultValue: 'Actual (W)' }),
        width: 120,
        type: 'number',
      },
      {
        field: 'deviation_percent',
        headerName: t('managementDashboard.columnDeviation', { defaultValue: 'Deviation' }),
        width: 120,
        type: 'number',
        valueFormatter: value => (value != null ? `+${value}%` : ''),
      },
    ],
    [terms, t],
  );

  const lopColumns = useMemo(
    () => [
      { field: 'site_name', headerName: terms.site, flex: 1, minWidth: 150 },
      {
        field: 'timestamp',
        headerName: t('managementDashboard.columnTime', { defaultValue: 'Time' }),
        width: 180,
        valueFormatter: value => (value ? new Date(value).toLocaleString() : ''),
      },
      {
        field: 'duration_hours',
        headerName: t('managementDashboard.columnDurationHours', { defaultValue: 'Duration (hrs)' }),
        width: 130,
        type: 'number',
        valueFormatter: value =>
          value != null ? `${value}h` : t('managementDashboard.ongoing', { defaultValue: 'Ongoing' }),
      },
    ],
    [terms, t],
  );

  const customerName = customer?.name ?? '';
  const pageTitle = t('managementDashboard.pageTitle', { defaultValue: 'Management Dashboard' });

  const changePct = data?.energy_consumption?.change_percent;
  const changeColor = changePct < 0 ? 'success.main' : changePct > 0 ? 'error.main' : 'text.secondary';
  const changeText = changePct != null ? `${changePct > 0 ? '+' : ''}${changePct}% vs prior period` : null;
  const savingsImplemented = data?.savings?.implemented;

  return (
    <AppNav {...props} loading={loading} page="customer_management_dashboard" notFullHeight>
      <Box sx={{ mx: 2, pb: 3 }}>
        <TitleBar
          showBackButton
          title={pageTitle}
          items={<CustomerTitleItems user={user} customer={customer} params={params} page="dashboards" />}
        />

        {/* Period Selector */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <ToggleButtonGroup value={period} exclusive onChange={handlePeriodChange} size="small">
            {PERIOD_OPTIONS.map(opt => (
              <ToggleButton key={opt.value} value={opt.value}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(error)}
          </Alert>
        )}

        {/* Stat Cards */}
        <StatCardGroup columns={4} gap={2} sx={{ mb: 3 }}>
          <StatCard
            title={t('managementDashboard.estimatedEnergyUse', { defaultValue: 'Estimated Energy Use' })}
            value={
              data?.energy_consumption ? `${data.energy_consumption.current_period_kwh.toLocaleString()} kWh` : '-'
            }
            color={changeColor}
            loading={loading}
            icon={<BoltIcon fontSize="small" />}
            helperText={
              changeText
                ? `${changeText} · ${t('managementDashboard.estimatedFromPeakPower', { defaultValue: 'estimated from avg peak power' })}`
                : t('managementDashboard.estimatedFromPeakPower', { defaultValue: 'Estimated from avg peak power' })
            }
            tooltip={t('managementDashboard.tooltipEnergyUse', {
              defaultValue: 'Estimated kWh based on average peak power readings',
            })}
          />
          <StatCard
            title={t('managementDashboard.darkHoursSaved', { defaultValue: 'Dark Hours Saved' })}
            value={
              savingsImplemented
                ? (data?.savings?.dark_hours_saved ?? '-')
                : t('managementDashboard.pending', { defaultValue: 'Pending' })
            }
            color="success.main"
            loading={loading}
            icon={<DarkModeIcon fontSize="small" />}
            helperText={
              savingsImplemented
                ? null
                : t('managementDashboard.scheduleNotImplemented', {
                    defaultValue: 'Schedule savings calculation not implemented yet',
                  })
            }
            tooltip={t('managementDashboard.tooltipDarkHours', {
              defaultValue: 'Hours saved vs default lighting schedules',
            })}
          />
          <StatCard
            title={t('managementDashboard.occupancyRate', { defaultValue: 'Occupancy Rate' })}
            value={data?.occupancy ? `${data.occupancy.occupancy_rate}%` : '-'}
            color="primary.main"
            loading={loading}
            icon={<HomeWorkIcon fontSize="small" />}
            helperText={
              data?.occupancy ? `${data.occupancy.occupied_boards} of ${data.occupancy.total_boards} boards` : null
            }
            tooltip={t('managementDashboard.tooltipOccupancy', {
              defaultValue: 'Percentage of boards with active contracts',
            })}
          />
          <StatCard
            title={t('managementDashboard.lopEvents', { defaultValue: 'LoP Events' })}
            value={data?.lop_report?.total_lop_events ?? '-'}
            color="error.main"
            loading={loading}
            icon={<SavingsIcon fontSize="small" />}
            helperText={data?.lop_report ? `${data.lop_report.affected_sites} sites affected` : null}
            tooltip={t('managementDashboard.tooltipLopEvents', {
              defaultValue: 'Loss of power events in the selected period',
            })}
          />
        </StatCardGroup>

        {/* Energy Consumption by Site Group */}
        {energyChartData.length > 0 && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('managementDashboard.estimatedEnergyByPlant', { defaultValue: 'Estimated Energy by Plant' })}
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={energyChartData} margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis label={{ value: 'kWh', angle: -90, position: 'insideLeft' }} />
                  <Tooltip
                    formatter={val => [`${val} kWh`, t('managementDashboard.energy', { defaultValue: 'Energy' })]}
                  />
                  <Bar dataKey="kwh" fill="#2196f3" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Noisy Structures & Outliers side by side */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('managementDashboard.noisyStructures', { defaultValue: 'Noisy Structures' })}
                </Typography>
                <DataGridPro
                  rows={data?.noisy_structures || []}
                  columns={noisyColumns}
                  getRowId={row => row.site_id}
                  autoHeight
                  density="compact"
                  disableRowSelectionOnClick
                  pagination
                  pageSizeOptions={[10, 25]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  sx={{ border: 'none' }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('managementDashboard.powerOutliers', { defaultValue: 'Power Outliers' })}
                </Typography>
                <DataGridPro
                  rows={data?.outliers || []}
                  columns={outlierColumns}
                  getRowId={row => row.site_id}
                  autoHeight
                  density="compact"
                  disableRowSelectionOnClick
                  pagination
                  pageSizeOptions={[10, 25]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  sx={{ border: 'none' }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* LED vs Halogen */}
        {ledVsHalogenData.length > 0 && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('managementDashboard.ledVsHalogen', { defaultValue: 'LED vs Halogen/HPS Comparison' })}
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={ledVsHalogenData} margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    yAxisId="count"
                    orientation="left"
                    label={{
                      value: t('managementDashboard.axisCount', { defaultValue: 'Count' }),
                      angle: -90,
                      position: 'insideLeft',
                    }}
                  />
                  <YAxis
                    yAxisId="watts"
                    orientation="right"
                    label={{
                      value: t('managementDashboard.axisAvgWatts', { defaultValue: 'Avg Watts' }),
                      angle: 90,
                      position: 'insideRight',
                    }}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar
                    yAxisId="count"
                    dataKey="count"
                    fill="#2196f3"
                    name={t('managementDashboard.axisCount', { defaultValue: 'Count' })}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    yAxisId="watts"
                    dataKey="avgWatts"
                    fill="#ff9800"
                    name={t('managementDashboard.axisAvgWatts', { defaultValue: 'Avg Watts' })}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* LoP Report */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('managementDashboard.lopReport', { defaultValue: 'Loss of Power Report' })}
            </Typography>
            <DataGridPro
              rows={data?.lop_report?.events || []}
              columns={lopColumns}
              getRowId={row => `${row.site_id}-${row.timestamp}`}
              autoHeight
              density="compact"
              disableRowSelectionOnClick
              pagination
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              sx={{ border: 'none' }}
            />
          </CardContent>
        </Card>
      </Box>
    </AppNav>
  );
}

export default ManagementDashboardPage;
